import { fg } from '@atlaskit/platform-feature-flags';
import { getThirdPartyFilterOptions } from '@atlassian/search-client/third-party-filter-options';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys3P } from '../../../../../common/constants/products';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('caseStatus');

const fetchDynamicCaseStatusOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getThirdPartyFilterOptions({
			cloudId,
			datasourceId,
			aggAbsoluteUrl: config.aggAbsoluteUrl,
		});

		if (!result) {
			return [];
		}

		const caseStatuses = result.filters.find((f) => f.name === 'caseStatuses');
		if (!caseStatuses || caseStatuses.values.length === 0) {
			return [];
		}

		return caseStatuses.values.map((status) => ({
			value: status.name,
			trackingKey: status.name,
			queryParamValue: status.name,
			isDefault: false,
			label: status.label ?? status.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch {
		return [];
	}
};

/**
 * Returns case status options — dynamic when the feature flag is on, and
 * an empty list when the feature flag is off (filter won't load options).
 */
const fetchCaseStatusOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	if (fg('rovo-fps-3p-ux-sf-case-status-filter')) {
		return fetchDynamicCaseStatusOptions(config);
	}
	return [];
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchCaseStatusOptions(config);
	const selected = new Set(queryParams);
	return allOptions.filter((option) => selected.has(option.queryParamValue));
};

export const filter: CreatedSelectFilter<'caseStatus'> = createMultiSelectFilter({
	id: 'caseStatus',
	products: [...salesforceProducts, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'caseStatuses',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchCaseStatusOptions(config);
		},
	},
});
